import * as THREE from 'three';
import type { ModelDocument, ModelEntity } from '../model/types';

function wallEndpoints(entity: ModelEntity): THREE.Vector3[] {
  const half = entity.dimensions.width * Math.abs(entity.scale[0]) / 2;
  const direction = new THREE.Vector3(1, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), entity.rotation[1]);
  const centre = new THREE.Vector3(entity.position[0], 0, entity.position[2]);
  return [centre.clone().addScaledVector(direction, half), centre.clone().addScaledVector(direction, -half)];
}

function footprintCorners(entity: ModelEntity): THREE.Vector3[] {
  const width = entity.dimensions.width * Math.abs(entity.scale[0]);
  const depth = entity.dimensions.depth * Math.abs(entity.scale[2]);
  const centre = new THREE.Vector3(entity.position[0], 0, entity.position[2]);
  const points = [
    new THREE.Vector3(-width / 2, 0, -depth / 2),
    new THREE.Vector3(width / 2, 0, -depth / 2),
    new THREE.Vector3(width / 2, 0, depth / 2),
    new THREE.Vector3(-width / 2, 0, depth / 2),
  ];
  return points.map((point) => point.applyAxisAngle(new THREE.Vector3(0, 1, 0), entity.rotation[1]).add(centre));
}

export function snapGroundPoint(point: THREE.Vector3, document: ModelDocument): THREE.Vector3 {
  const result = point.clone();
  result.y = 0;
  if (document.settings.snapEnabled) {
    const grid = Math.max(0.001, document.settings.gridSize);
    result.x = Math.round(result.x / grid) * grid;
    result.z = Math.round(result.z / grid) * grid;
  }

  let closest: THREE.Vector3 | null = null;
  let closestDistance = 0.18;
  for (const entity of document.entities) {
    if (!entity.visible || entity.kind === 'line') continue;
    const candidates = entity.kind === 'wall' ? wallEndpoints(entity) : footprintCorners(entity);
    for (const candidate of candidates) {
      const distance = candidate.distanceTo(result);
      if (distance < closestDistance) {
        closest = candidate;
        closestDistance = distance;
      }
    }
  }
  return closest ?? result;
}

export function inferAxis(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3 {
  const delta = end.clone().sub(start);
  const angle = Math.atan2(Math.abs(delta.z), Math.abs(delta.x));
  const threshold = THREE.MathUtils.degToRad(7);
  if (angle < threshold) end.z = start.z;
  if (Math.PI / 2 - angle < threshold) end.x = start.x;
  return end;
}
